/**
 * Modal Management System
 *
 * This module provides a centralized way to manage modals in the TON Survivors app.
 * It allows both React components and Phaser game code to open modals without
 * creating DOM nodes directly.
 *
 * Usage from Phaser:
 *   import {
 *     showBoostPurchaseDialog,
 *     showNarration,
 *     showSpecialDealsModal,
 *     showEnergyPopup,
 *     showStageSelect
 *   } from '../../../utils/modal/ModalManager';
 *   showBoostPurchaseDialog(BoostTypeEnum.Explosion, () => console.log('Success!'));
 *   showNarration('narration-id', narrationEntry, handleResponse);
 *   showSpecialDealsModal(() => console.log('Purchase success!'), false);
 *   showEnergyPopup(() => console.log('Energy popup closed'));
 *   showStageSelect();
 *
 * Usage from React:
 *   const { openModal } = useModalManager();
 *   openModal(ModalType.BOOST_PURCHASE, { boostType: BoostTypeEnum.Explosion });
 *   openModal(ModalType.NARRATION, { narrationId: 'id', narrationEntry });
 *   openModal(ModalType.SPECIAL_DEALS, { onPurchaseSuccess: callback, hideShopButton: false });
 *   openModal(ModalType.ENERGY_POPUP, { onClose: callback });
 *   openModal(ModalType.STAGE_SELECT, {});
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { BoostPurchaseDialog } from "../../components/BoostPurchaseDialog/BoostPurchaseDialog";
import { CharacterLooks } from "../../components/Looks/CharacterLooks";
import { NarrationComponent } from "../../components/Narration/NarrationComponent";
import { SpecialDealsModal } from "../../components/SpecialDealsModal/SpecialDealsModal";
import { EnergyPopup } from "../../components/EnergyPopup/EnergyPopup";
import { InvitePromoModal } from "../../components/InvitePromoModal/InvitePromoModal";
import { BookPromoModal } from "../../components/BookPromoModal/BookPromoModal";
import { StageSelect } from "../../components/StageSelect/StageSelect";
import { Onboarding } from "../../components/Onboarding/Onboarding";
import { ManaGuideOverlay } from "../../components/Onboarding/ManaGuideOverlay";
import { BoostTypeEnum } from "../../handlers/types";
import { NarrationEntry } from "../../components/Narration/narrationTypes";
import StageType from "../../scripts/enums/StageType";
import Game from "../../scripts/game/Game";
import Scenes from "../../scripts/enums/Scenes";
import {
  disableCanvasInteractivity,
  enableCanvasInteractivity,
} from "../../scripts";
import { StorageService } from "../../services/LocalStorageService";
import { useGameStore } from "../../store/gameStore";
import { backButton } from "@telegram-apps/sdk";

// localStorage key for tracking shown looks selectors
const LOOKS_SELECTOR_SHOWN_KEY = "stage-looks-selector-shown";

// Modal types
export enum ModalType {
  BOOST_PURCHASE = "BOOST_PURCHASE",
  CHARACTER_LOOKS = "CHARACTER_LOOKS",
  STAGE_LOOKS = "STAGE_LOOKS",
  NARRATION = "NARRATION",
  SPECIAL_DEALS = "SPECIAL_DEALS",
  ENERGY_POPUP = "ENERGY_POPUP",
  STAGE_SELECT = "STAGE_SELECT",
  ONBOARDING = "ONBOARDING",
  INVITE_PROMO = "INVITE_PROMO",
  MANA_GUIDE = "MANA_GUIDE",
  BOOK_PROMO = "BOOK_PROMO",
}

// Modal data interfaces
interface BoostPurchaseModalData {
  boostType:
    | BoostTypeEnum.Explosion
    | BoostTypeEnum.Heal
    | BoostTypeEnum.Revive;
  onPurchaseSuccess?: () => void;
}

interface CharacterLooksModalData {
  characterId: string;
  onComplete?: () => void;
}

interface StageLooksModalData {
  stageType: StageType;
  onComplete?: () => void;
}

interface NarrationModalData {
  narrationId: string;
  narrationEntry: NarrationEntry;
  onResponseSelected?: (nextId: string | null) => void;
  onComplete?: () => void;
  isTransitioning?: boolean;
  previousNarrationId?: string | null;
}

interface SpecialDealsModalData {
  onPurchaseSuccess?: () => void;
  hideShopButton?: boolean;
}

interface EnergyPopupModalData {
  onClose?: () => void;
}

interface StageSelectModalData {
  // No specific data needed for stage select
}

interface OnboardingModalData {
  // No additional data needed
}

interface InvitePromoModalData {
  // No additional data needed
}

interface BookPromoModalData {
  onPurchaseSuccess?: () => void;
}

type ModalData =
  | BoostPurchaseModalData
  | CharacterLooksModalData
  | StageLooksModalData
  | NarrationModalData
  | SpecialDealsModalData
  | EnergyPopupModalData
  | StageSelectModalData
  | OnboardingModalData
  | InvitePromoModalData
  | BookPromoModalData;

interface ModalState {
  type: ModalType | null;
  data: ModalData | null;
  isOpen: boolean;
}

interface QueuedModal {
  type: ModalType;
  data: ModalData;
}

interface ModalManagerContextType {
  openModal: (type: ModalType, data: ModalData) => void;
  closeModal: () => void;
  modalState: ModalState;
}

const ModalManagerContext = createContext<ModalManagerContextType | undefined>(
  undefined
);

// Global reference for non-React code
let globalModalManager: ModalManagerContextType | null = null;

export function ModalManagerProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalState] = useState<ModalState>({
    type: null,
    data: null,
    isOpen: false,
  });
  const [modalQueue, setModalQueue] = useState<QueuedModal[]>([]);

  const openModal = useCallback(
    (type: ModalType, data: ModalData) => {
      // If a modal is already open, check if it's the same type
      if (modalState.isOpen && modalState.type) {
        // For narration modals, update the current modal instead of queueing
        // This allows narration chaining to work properly
        if (
          modalState.type === ModalType.NARRATION &&
          type === ModalType.NARRATION
        ) {
          setModalState({
            type,
            data,
            isOpen: true,
          });
          return;
        }

        // For other modal types, add to queue as before
        setModalQueue((prev) => [...prev, { type, data }]);
        return;
      }

      // Pause game and disable canvas interactivity (except for StageSelect)
      if (type !== ModalType.STAGE_SELECT) {
        if (Game.Core?.SceneManager?.scene) {
          try {
            Game.Core.SceneManager.scene.pause(Scenes.MainScene);
          } catch (error) {
            console.warn("Could not pause MainScene:", error);
          }
        }
        disableCanvasInteractivity();
      }

      setModalState({
        type,
        data,
        isOpen: true,
      });
    },
    [modalState]
  );

  const closeModal = useCallback(() => {
    const currentModalType = modalState.type;

    // Resume game and enable canvas interactivity (except for StageSelect)
    if (currentModalType !== ModalType.STAGE_SELECT) {
      if (Game.Core?.SceneManager?.scene) {
        try {
          Game.Core.SceneManager.scene.resume(Scenes.MainScene);
        } catch (error) {
          console.warn("Could not resume MainScene:", error);
        }
      }
      enableCanvasInteractivity();
    } else {
      // For StageSelect, navigate back to main menu as per original behavior
      Game.Core.SceneManager.NavigateToScene(Scenes.MainMenuScene);
    }

    // Check if there are queued modals
    if (modalQueue.length > 0) {
      const nextModal = modalQueue[0];
      const remainingQueue = modalQueue.slice(1);

      // Update queue first
      setModalQueue(remainingQueue);

      // Then open the next modal
      setModalState({
        type: nextModal.type,
        data: nextModal.data,
        isOpen: true,
      });

      // Handle game pause for the new modal
      if (nextModal.type !== ModalType.STAGE_SELECT) {
        if (Game.Core?.SceneManager?.scene) {
          try {
            Game.Core.SceneManager.scene.pause(Scenes.MainScene);
          } catch (error) {
            console.warn("Could not pause MainScene:", error);
          }
        }
        disableCanvasInteractivity();
      }
    } else {
      // No queued modals, just close
      setModalState({
        type: null,
        data: null,
        isOpen: false,
      });
    }
  }, [modalState.type, modalQueue]);

  const contextValue = {
    openModal,
    closeModal,
    modalState,
  };

  // Set global reference
  globalModalManager = contextValue;

  return (
    <ModalManagerContext.Provider value={contextValue}>
      {children}
      <ModalRenderer />
    </ModalManagerContext.Provider>
  );
}

function ModalRenderer() {
  const { modalState, closeModal } = useModalManager();

  if (!modalState.isOpen || !modalState.type) {
    return null;
  }

  switch (modalState.type) {
    case ModalType.BOOST_PURCHASE: {
      const data = modalState.data as BoostPurchaseModalData;
      return (
        <BoostPurchaseDialog
          isOpen={true}
          onClose={closeModal}
          boostType={data.boostType}
          onPurchaseSuccess={() => {
            data.onPurchaseSuccess?.();
            closeModal();
          }}
        />
      );
    }

    case ModalType.CHARACTER_LOOKS:
    case ModalType.STAGE_LOOKS: {
      const data = modalState.data as CharacterLooksModalData;
      return (
        <CharacterLooks
          characterId={data.characterId}
          onComplete={() => {
            data.onComplete?.();
            closeModal();
          }}
        />
      );
    }

    case ModalType.NARRATION: {
      const data = modalState.data as NarrationModalData;
      return (
        <NarrationComponent
          narrationId={data.narrationId}
          narrationEntry={data.narrationEntry}
          onResponseSelected={(nextId) => {
            if (data.onResponseSelected) {
              data.onResponseSelected(nextId);
            }

            // Always close modal when there's no next narration
            if (nextId === null) {
              data.onComplete?.();
              closeModal();
            }
          }}
          isTransitioning={data.isTransitioning}
          previousNarrationId={data.previousNarrationId}
        />
      );
    }

    case ModalType.SPECIAL_DEALS: {
      const data = modalState.data as SpecialDealsModalData;
      return (
        <SpecialDealsModal
          isOpen={true}
          onClose={closeModal}
          onPurchaseSuccess={() => {
            data.onPurchaseSuccess?.();
            closeModal();
          }}
          hideShopButton={data.hideShopButton}
        />
      );
    }

    case ModalType.ENERGY_POPUP: {
      const data = modalState.data as EnergyPopupModalData;
      return (
        <EnergyPopup
          isOpen={true}
          onClose={() => {
            data.onClose?.();
            closeModal();
          }}
        />
      );
    }

    case ModalType.STAGE_SELECT: {
      // StageSelect component handles its own navigation internally
      // When it needs to close, it should call closeCurrentModal()
      return <StageSelect />;
    }

    case ModalType.ONBOARDING: {
      return (
        <Onboarding
          isOpen={true}
          onClose={() => {
            const completed =
              localStorage.getItem("onboarding_shown_v2") === "true";
            // Close current modal first so the next one mounts cleanly
            closeModal();
            // TEMPORARILY DISABLED: Mana guide overlay
            // if (completed) {
            //   // Open the mana guide on the next tick after closing
            //   setTimeout(() => {
            //     globalModalManager?.openModal(ModalType.MANA_GUIDE, {} as any);
            //   }, 0);
            // }
          }}
        />
      );
    }

    case ModalType.MANA_GUIDE: {
      // Render the mana guide overlay as a modal layer
      return <ManaGuideOverlay onClose={closeModal} />;
    }

    case ModalType.INVITE_PROMO: {
      return <InvitePromoModal isOpen={true} onClose={closeModal} />;
    }

    case ModalType.BOOK_PROMO: {
      const data = modalState.data as BookPromoModalData;
      return (
        <BookPromoModal
          isOpen={true}
          onClose={closeModal}
          onPurchaseSuccess={() => {
            data.onPurchaseSuccess?.();
            closeModal();
          }}
        />
      );
    }

    default:
      return null;
  }
}

export function useModalManager() {
  const context = useContext(ModalManagerContext);
  if (context === undefined) {
    throw new Error(
      "useModalManager must be used within a ModalManagerProvider"
    );
  }
  return context;
}

// Global functions for Phaser to call
export function showBoostPurchaseDialog(
  boostType:
    | BoostTypeEnum.Explosion
    | BoostTypeEnum.Heal
    | BoostTypeEnum.Revive,
  onPurchaseSuccess?: () => void
): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  globalModalManager.openModal(ModalType.BOOST_PURCHASE, {
    boostType,
    onPurchaseSuccess: async () => {
      onPurchaseSuccess?.();

      // Update user data after successful purchase
      const store = useGameStore.getState();
      await store.refreshUserData();
    },
  });
}

export function showCharacterLooks(
  characterId: string,
  onComplete?: () => void
): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  globalModalManager.openModal(ModalType.CHARACTER_LOOKS, {
    characterId,
    onComplete,
  });
}

export function showStageLooks(
  options: { stageType: StageType; force?: boolean } = {
    stageType: StageType.DESERT1,
  }
): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  // Check if looks are available in the store
  const store = useGameStore.getState();
  if (!store.isInitialized || !store.userLooks.length) {
    console.error("Store not initialized or no looks available");
    return;
  }

  // Check if selector has already been shown for this stage
  if (!options.force && hasLooksSelectorBeenShown(options.stageType)) {
    // Skip showing the selector and go directly to onboarding
    if (Game.Core?.PlayerOptions) {
      Game.Core.PlayerOptions.SelectedStage = options.stageType;
    }
    return;
  }

  // Get the current character ID
  const characterId = Game.Core?.PlayerOptions?.SelectedCharacterId || "";

  globalModalManager.openModal(ModalType.STAGE_LOOKS, {
    characterId,
    onComplete: () => {
      // Mark this stage's looks selector as shown
      markLooksSelectorAsShown(options.stageType);

      // Store the stage type in PlayerOptions after closing
      if (Game.Core?.PlayerOptions) {
        Game.Core.PlayerOptions.SelectedStage = options.stageType;
      }
    },
  });
}

export function showNarration(
  narrationId: string,
  narrationEntry: NarrationEntry,
  onResponseSelected?: (nextId: string | null) => void,
  onComplete?: () => void,
  isTransitioning?: boolean,
  previousNarrationId?: string | null
): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  globalModalManager.openModal(ModalType.NARRATION, {
    narrationId,
    narrationEntry,
    onResponseSelected,
    onComplete,
    isTransitioning,
    previousNarrationId,
  });
}

export function closeCurrentModal(): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  globalModalManager.closeModal();
}

export function showSpecialDealsModal(
  onPurchaseSuccess?: () => void,
  hideShopButton?: boolean
): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  // Mark special deals as shown today when modal is displayed
  StorageService.markSpecialDealsShownToday();

  globalModalManager.openModal(ModalType.SPECIAL_DEALS, {
    onPurchaseSuccess: async () => {
      onPurchaseSuccess?.();

      // Update user data after successful purchase
      const store = useGameStore.getState();
      await store.refreshUserData();
    },
    hideShopButton,
  });
}

export function showEnergyPopup(onCloseCallback?: () => void): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  globalModalManager.openModal(ModalType.ENERGY_POPUP, {
    onClose: onCloseCallback,
  });
}

export function showStageSelect(): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  backButton.show();
  backButton.onClick(() => {
    globalModalManager?.closeModal();
    backButton.hide();
  });

  globalModalManager.openModal(ModalType.STAGE_SELECT, {});
}

/**
 * Show onboarding modal
 */
export function showOnboarding(): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  globalModalManager.openModal(ModalType.ONBOARDING, {});
}

/**
 * Show invite promo modal and mark as shown today
 */
export function showInvitePromoModal(): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  StorageService.markInvitePromoShownToday();
  globalModalManager.openModal(ModalType.INVITE_PROMO, {});
}

/**
 * Show book promo modal and mark as shown today
 */
export function showBookPromoModal(onPurchaseSuccess?: () => void): void {
  if (!globalModalManager) {
    console.error("ModalManager not initialized");
    return;
  }

  StorageService.markBookPromoShownToday();
  globalModalManager.openModal(ModalType.BOOK_PROMO, {
    onPurchaseSuccess: async () => {
      onPurchaseSuccess?.();

      // Update user data after successful purchase
      const store = useGameStore.getState();
      await store.refreshUserData();
    },
  });
}

// Utility functions for stage looks tracking

/**
 * Check if looks selector has been shown for a specific stage
 * @param stageType The stage type to check
 * @returns True if the looks selector has been shown before
 */
export function hasLooksSelectorBeenShown(stageType: StageType): boolean {
  try {
    const shownStages = JSON.parse(
      localStorage.getItem(LOOKS_SELECTOR_SHOWN_KEY) || "[]"
    ) as string[];
    return shownStages.includes(stageType);
  } catch (error) {
    console.error(
      "Error reading looks selector state from localStorage:",
      error
    );
    return false;
  }
}

/**
 * Mark looks selector as shown for a specific stage
 * @param stageType The stage type to mark as shown
 */
function markLooksSelectorAsShown(stageType: StageType): void {
  try {
    const shownStages = JSON.parse(
      localStorage.getItem(LOOKS_SELECTOR_SHOWN_KEY) || "[]"
    ) as string[];
    if (!shownStages.includes(stageType)) {
      shownStages.push(stageType);
      localStorage.setItem(
        LOOKS_SELECTOR_SHOWN_KEY,
        JSON.stringify(shownStages)
      );
    }
  } catch (error) {
    console.error("Error saving looks selector state to localStorage:", error);
  }
}

/**
 * Reset the looks selector shown state for a specific stage or all stages
 * @param stageType Optional stage type to reset. If not provided, resets for all stages.
 */
export function resetLooksSelectorShownState(stageType?: StageType): void {
  try {
    if (stageType) {
      // Reset for a specific stage
      const shownStages = JSON.parse(
        localStorage.getItem(LOOKS_SELECTOR_SHOWN_KEY) || "[]"
      ) as string[];
      const updatedStages = shownStages.filter((stage) => stage !== stageType);
      localStorage.setItem(
        LOOKS_SELECTOR_SHOWN_KEY,
        JSON.stringify(updatedStages)
      );
    } else {
      // Reset for all stages
      localStorage.removeItem(LOOKS_SELECTOR_SHOWN_KEY);
    }
    console.log(
      `Looks selector shown state reset ${
        stageType ? `for stage ${stageType}` : "for all stages"
      }`
    );
  } catch (error) {
    console.error("Error resetting looks selector state:", error);
  }
}
